import { Component, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import Quill from 'quill';
import { LambdaService } from 'src/app/lambda.service';
import { ThemingService } from 'src/app/theming.service';
import {
  applyDynamicValidators,
  buildVisibleFields,
  ComponentInfoFormValue,
  createComponentForm,
  createInfoFieldGroup,
  hasMeaningfulText,
  isYouTubeUrl,
  sanitizeString,
  serializeInfoFields,
  VisibleFieldKey,
} from '../shared/component-form.helpers';

declare let $: any;

@Component({
  selector: 'app-create-component',
  templateUrl: './create-component.component.html',
  styleUrls: ['./create-component.component.scss'],
})
export class CreateComponentComponent implements OnInit {
  @Output() creationSuccess = new EventEmitter<void>();
  @Input() pageId: string | null = null;

  componentForm: FormGroup;
  componentTypes: any[] = [];
  groupedDesigns: Record<number, any[]> = {};
  uniqueParentIds: any[] = [];
  categories: any[] = [];
  collections: any[] = [];
  subCategories: any[] = [];
  subSubCategories: any[] = [];
  clientSubscriptions: any[] = [];

  loadingState: 'idle' | 'initializing' | 'saving' = 'idle';
  pendingSuccessAcknowledgement = false;
  selectedParentId = 0;
  selectedDesign: any = null;
  selectedDesignDescription = '';
  selectedDesignImageUrl = '';
  selectedOption = '';
  productComponentType = 0;
  designEnabled = false;
  minItems = 0;
  maxItems = 0;
  errorMessage = '';
  itemListId = '';

  activeSubscriptions = 0;
  selectedSubscriptions: number[] = [];

  currentStep = 1;
  selectedIndex = 0;
  submitAttempted = false;
  navigationAttempted = false;
  visibleFields = new Set<VisibleFieldKey>();

  readonly maxLength = 2500;
  readonly infoMaxLength = 2500;
  remainingCharacters = this.maxLength;
  infoCharacterCounts: number[] = [];
  activeLayoutPreview: any | null = null;
  layoutPreviewStyle: Record<string, string> = {};
  previewModalDesign: any | null = null;
  usesTapPreviewMode = false;
  private hideLayoutPreviewTimer: ReturnType<typeof setTimeout> | null = null;

  readonly steps = [
    { index: 1, label: 'COMPONENT_STEP_1_LABEL', description: 'COMPONENT_STEP_1_DESCRIPTION' },
    { index: 2, label: 'COMPONENT_STEP_2_LABEL', description: 'COMPONENT_STEP_2_DESCRIPTION' },
    { index: 3, label: 'COMPONENT_STEP_3_LABEL', description: 'COMPONENT_STEP_3_DESCRIPTION' },
    { index: 4, label: 'COMPONENT_STEP_4_LABEL', description: 'COMPONENT_STEP_4_DESCRIPTION' },
  ];

  parentIdNames: { [key: number]: string } = {
    1: 'BANNERS',
    2: 'CARDS',
    3: 'PRODUCTS',
  };

  modules = {
    'emoji-shortname': true,
    'emoji-textarea': true,
    'emoji-toolbar': true,
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],
      [{ header: 1 }, { header: 2 }],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ script: 'sub' }, { script: 'super' }],
      [{ indent: '-1' }, { indent: '+1' }],
      [{ direction: 'rtl' }],
      [{ size: ['small', false, 'large', 'huge'] }],
      [{ color: [] }, { background: [] }],
      [{ align: [] }],
      ['link'],
      ['emoji'],
      ['clean'],
      [
        {
          font: [
            'arial',
            'inter',
            'lato',
            'merriweather',
            'montserrat',
            'nunito',
            'pacifico',
            'poppins',
            'raleway',
            'roboto',
            'serif',
          ],
        },
      ],
    ],
  };

  constructor(
    private fb: FormBuilder,
    private lambdaService: LambdaService,
    private themingService: ThemingService,
    private sanitizer: DomSanitizer,
    private translate: TranslateService
  ) {
    this.componentForm = createComponentForm(this.fb);
  }

  get componentGroup(): FormGroup {
    return this.componentForm.get('component') as FormGroup;
  }

  get infoFields(): FormArray {
    return this.componentForm.get('infoFields') as FormArray;
  }

  get selectedInfoField(): FormGroup | null {
    return (this.infoFields.at(this.selectedIndex) as FormGroup) || null;
  }

  get layoutOptions(): any[] {
    return this.groupedDesigns[this.selectedParentId] || [];
  }

  get isLayoutSelectionDisabled(): boolean {
    return !this.selectedParentId || !this.designEnabled;
  }

  get isBusy(): boolean {
    return this.loadingState !== 'idle';
  }

  get isSaving(): boolean {
    return this.loadingState === 'saving';
  }

  get loadingMessage(): string {
    return this.isSaving ? 'SAVING' : 'PROCESSING';
  }

  get stepProgress(): number {
    return (this.currentStep / this.steps.length) * 100;
  }

  get usesInfoTabs(): boolean {
    return !!this.selectedDesign && this.selectedParentId !== 3;
  }

  get currentInfoRemainingCharacters(): number {
    const count = this.infoCharacterCounts[this.selectedIndex] || 0;
    return this.infoMaxLength - count;
  }

  ngOnInit(): void {
    this.componentGroup.patchValue({
      pageId: parseInt(this.pageId || '0', 10),
    });

    this.evaluatePreviewInteractionMode();

    this.loadingState = 'initializing';
    Promise.all([
      this.getComponentTypes(),
      this.getCategories(),
      this.getCollections(),
      this.getSubCategories(),
      this.getSubSubCategories(),
      this.getClientSubscriptions(),
      Promise.resolve(this.registerFonts()),
    ])
      .then(() => {
        this.loadingState = 'idle';
        this.updateVisibleFields();
      })
      .catch((error) => {
        console.error(error);
        this.loadingState = 'idle';
      });
  }

  registerFonts(): void {
    const FontAttributor = Quill.import('attributors/class/font');
    FontAttributor.whitelist = [
      'arial',
      'inter',
      'lato',
      'merriweather',
      'montserrat',
      'nunito',
      'pacifico',
      'poppins',
      'raleway',
      'roboto',
      'serif',
    ];

    Quill.register(FontAttributor, true);
  }

  async getComponentTypes(): Promise<void> {
    const response: any = await this.lambdaService.getComponentTypes(this.themingService.shopId);
    this.componentTypes = response.data;
    this.uniqueParentIds = this.getUniqueParentIds();
    this.groupedDesigns = this.getDesignsByParentId();
  }

  async getClientSubscriptions(): Promise<void> {
    try {
      const response: any = await this.lambdaService.getClientSubscriptions(this.themingService.shopId);
      this.clientSubscriptions = Array.isArray(response.subscriptions) ? response.subscriptions : [];
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      this.clientSubscriptions = [];
    }
  }

  async getCategories(): Promise<void> {
    try {
      const response: any = await this.lambdaService.getCategories(this.themingService.shopId);
      this.categories = Array.isArray(response[0]) ? response[0] : [];
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }

  async getCollections(): Promise<void> {
    try {
      const response: any = await this.lambdaService.getCollections(this.themingService.shopId);
      this.collections = Array.isArray(response.collections) ? response.collections : [];
    } catch (error) {
      console.error('Error fetching collections:', error);
      this.collections = [];
    }
  }

  async getSubCategories(): Promise<void> {
    try {
      const response: any = await this.lambdaService.getSubCategories(this.themingService.shopId);
      this.subCategories = Array.isArray(response.subCategories) ? response.subCategories : [];
    } catch (error) {
      console.error('Error fetching subCategories:', error);
    }
  }

  async getSubSubCategories(): Promise<void> {
    try {
      const response: any = await this.lambdaService.getSubSubCategories(this.themingService.shopId);
      this.subSubCategories = Array.isArray(response.subSubCategories) ? response.subSubCategories : [];
    } catch (error) {
      console.error('Error fetching subSubCategories:', error);
    }
  }

  onChangeParentId(event: Event): void {
    const value = Number((event.target as HTMLSelectElement).value || 0);
    this.selectedParentId = value;
    this.resetDynamicState();
    this.designEnabled = this.selectedParentId !== 3;
  }

  onOptionChange(option: string): void {
    this.selectedOption = option;
    this.resetSelections();

    switch (option) {
      case 'categories':
        this.productComponentType = 1;
        break;
      case 'subCategories':
        this.productComponentType = 2;
        break;
      case 'subSubcategories':
        this.productComponentType = 3;
        break;
      case 'collections':
        this.productComponentType = 4;
        break;
      default:
        this.productComponentType = 5;
        break;
    }

    this.designEnabled = this.selectedParentId === 3;
  }

  onCategoryChange(event: Event): void {
    const value = Number((event.target as HTMLSelectElement).value || 0);
    this.componentGroup.patchValue({
      categoryId: value,
      subCategoryId: 0,
      subSubCategoryId: 0,
      collectionId: 0,
    });
    this.designEnabled = value > 0;
  }

  onSubCategoryChange(event: Event): void {
    const value = Number((event.target as HTMLSelectElement).value || 0);
    this.componentGroup.patchValue({
      categoryId: 0,
      subCategoryId: value,
      subSubCategoryId: 0,
      collectionId: 0,
    });
    this.designEnabled = value > 0;
  }

  onSubSubCategoryChange(event: Event): void {
    const value = Number((event.target as HTMLSelectElement).value || 0);
    this.componentGroup.patchValue({
      categoryId: 0,
      subCategoryId: 0,
      subSubCategoryId: value,
      collectionId: 0,
    });
    this.designEnabled = value > 0;
  }

  onCollectionChange(event: Event): void {
    const value = Number((event.target as HTMLSelectElement).value || 0);
    this.componentGroup.patchValue({
      categoryId: 0,
      subCategoryId: 0,
      subSubCategoryId: 0,
      collectionId: value,
    });
    this.designEnabled = value > 0;
  }

  onItemQuantityChange(event: Event): void {
    const value = Number((event.target as HTMLSelectElement).value || 0);
    this.componentGroup.get('itemQuantity')?.setValue(value);
  }

  onLayoutSelection(componentTypeId: number): void {
    if (this.isLayoutSelectionDisabled) {
      return;
    }

    this.hideLayoutPreview();
    this.applyComponentTypeSelection(componentTypeId);
  }

  changeComponentTypeId(event: Event): void {
    const componentTypeId = Number((event.target as HTMLSelectElement).value || 0);
    this.applyComponentTypeSelection(componentTypeId);
  }

  updateVisibleFields(): void {
    const componentTypeId = Number(this.componentGroup.get('componentTypeId')?.value || 0);
    this.visibleFields = buildVisibleFields(componentTypeId);
    applyDynamicValidators(this.componentGroup, this.infoFields, this.visibleFields);
  }

  addNewField(validateLimit: boolean = true): void {
    if (validateLimit && this.infoFields.length >= this.maxItems) {
      this.errorMessage = this.translate.instant('COMPONENT_MAX_ITEMS_ERROR', { count: this.maxItems });
      $('#errorMaxMinItemModal').modal('show');
      return;
    }

    const group = createInfoFieldGroup(this.fb, this.visibleFields);
    this.infoFields.push(group);
    this.infoCharacterCounts.push(0);
    this.selectedIndex = this.infoFields.length - 1;
    applyDynamicValidators(this.componentGroup, this.infoFields, this.visibleFields);
    this.focusItemPanel(this.selectedIndex);
  }

  removeField(index: number): void {
    if (this.infoFields.length <= this.minItems) {
      this.errorMessage = this.translate.instant('COMPONENT_MIN_ITEMS_ERROR', { count: this.minItems });
      $('#errorMaxMinItemModal').modal('show');
      return;
    }

    this.infoFields.removeAt(index);
    this.infoCharacterCounts.splice(index, 1);
    this.selectedIndex = Math.max(0, Math.min(this.selectedIndex, this.infoFields.length - 1));
  }

  selectItem(index: number): void {
    this.selectedIndex = index;
  }

  nextStep(): void {
    this.navigationAttempted = true;
    if (!this.isStepValid(this.currentStep)) {
      this.markStepAsTouched(this.currentStep);
      return;
    }

    this.currentStep = Math.min(this.currentStep + 1, this.steps.length);
    this.navigationAttempted = false;
  }

  prevStep(): void {
    this.currentStep = Math.max(this.currentStep - 1, 1);
    this.navigationAttempted = false;
  }

  goToStep(target: number): void {
    if (target === this.currentStep) {
      return;
    }

    if (target < this.currentStep) {
      this.currentStep = target;
      this.navigationAttempted = false;
      return;
    }

    for (let step = this.currentStep; step < target; step += 1) {
      if (!this.isStepValid(step)) {
        this.currentStep = step;
        this.navigationAttempted = true;
        this.markStepAsTouched(step);
        return;
      }
    }

    this.currentStep = target;
    this.navigationAttempted = false;
  }

  async validateFormData(): Promise<void> {
    if (this.isSaving) {
      return;
    }

    this.submitAttempted = true;

    for (let step = 1; step <= this.steps.length; step += 1) {
      if (!this.isStepValid(step)) {
        this.currentStep = step;
        this.markStepAsTouched(step);
        return;
      }
    }

    this.loadingState = 'saving';
    try {
      await this.uploadComponentFiles();
      const component = this.componentGroup.getRawValue();
      const infoFields = this.infoFields.getRawValue() as ComponentInfoFormValue[];
      const serialized = serializeInfoFields(infoFields);
      const translatedContent = undefined;

      await this.lambdaService.createComponent(
        component.componentId,
        this.themingService.shopId,
        sanitizeString(component.componentName),
        sanitizeString(component.title),
        sanitizeString(component.subtitle),
        component.position,
        component.status,
        sanitizeString(component.description),
        sanitizeString(component.shopContactMail),
        component.componentTypeId,
        Number(component.itemQuantity || 0),
        component.itemListId,
        component.itemSort,
        serialized.imageUrls,
        serialized.descriptions,
        serialized.redirectUrls,
        serialized.infoTitles,
        serialized.urlNames,
        Number(component.collectionId || 0),
        Number(component.categoryId || 0),
        Number(component.subCategoryId || 0),
        Number(component.subSubCategoryId || 0),
        this.productComponentType,
        serialized.videoUrls,
        serialized.videoUrlTypes,
        parseInt(this.pageId || '0', 10),
        this.resolveSubscriptionsAllowed(),
        translatedContent
      );

      this.pendingSuccessAcknowledgement = true;
      this.loadingState = 'idle';
      $('#successcomponentModal').modal({ backdrop: 'static', keyboard: false });
      $('#successcomponentModal').modal('show');
    } catch (error) {
      console.error(error);
      this.loadingState = 'idle';
      $('#errorcomponentModal').modal('show');
    }
  }

  descriptionChanged(event: any): void {
    if (event.event !== 'text-change') {
      return;
    }

    const quill = event.editor;
    const textLength = Math.max(quill.getLength() - 1, 0);
    this.remainingCharacters = this.maxLength - textLength;

    if (textLength > this.maxLength) {
      quill.deleteText(this.maxLength, textLength - this.maxLength);
      this.remainingCharacters = 0;
    }
  }

  infoDescriptionChanged(event: any, index: number): void {
    if (event.event !== 'text-change') {
      return;
    }

    const quill = event.editor;
    const textLength = Math.max(quill.getLength() - 1, 0);
    this.infoCharacterCounts[index] = textLength;
    if (textLength > this.infoMaxLength) {
      quill.deleteText(this.infoMaxLength, textLength - this.infoMaxLength);
      this.infoCharacterCounts[index] = this.infoMaxLength;
    }
  }

  toggle(): void {
    this.activeSubscriptions = this.activeSubscriptions === 1 ? 0 : 1;
    if (this.activeSubscriptions === 0) {
      this.selectedSubscriptions = [];
    }
  }

  toggleSuscripcion(id: number): void {
    const index = this.selectedSubscriptions.indexOf(id);
    if (index > -1) {
      this.selectedSubscriptions.splice(index, 1);
    } else {
      this.selectedSubscriptions.push(id);
    }
  }

  triggerFileInput(index: number, type: 'image' | 'video'): void {
    const inputId = type === 'image' ? `btn-upload-image-${index}` : `btn-upload-video-${index}`;
    const uploadButton = document.getElementById(inputId) as HTMLInputElement;
    uploadButton?.click();
  }

  async handleFileSelection(index: number, event: Event, type: 'image' | 'video'): Promise<void> {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) {
      return;
    }

    const maxFileSize = 12 * 1024 * 1024;
    if (file.size > maxFileSize) {
      $('#errorCreateFileSizeModal').modal('show');
      return;
    }

    const fieldGroup = this.infoFields.at(index) as FormGroup;

    if (type === 'image') {
      try {
        const webpFile = await this.convertImageToWebP(file, 1920, 0.8);
        const tempURL = URL.createObjectURL(webpFile);
        const key = `${this.themingService.shopId}/${environment.stage}/images/components/component_${Date.now()}.webp`;
        fieldGroup.patchValue({
          imageURL: tempURL,
          blob: { file: webpFile, key },
        });
      } catch (error) {
        console.error('Error al convertir a WebP:', error);
        const extension = file.name.split('.').pop();
        const tempURL = URL.createObjectURL(file);
        const key = `${this.themingService.shopId}/${environment.stage}/images/components/component_${Date.now()}.${extension}`;
        fieldGroup.patchValue({
          imageURL: tempURL,
          blob: { file, key },
        });
      }
    } else {
      const extension = file.name.split('.').pop();
      const tempURL = URL.createObjectURL(file);
      const key = `${this.themingService.shopId}/${environment.stage}/videos/components/component_${Date.now()}.${extension}`;
      fieldGroup.patchValue({
        videoURL: tempURL,
        videoBlob: { file, key },
        videoManualURL: '',
        videoUrlType: 1,
        isYouTube: false,
      });
    }

  }

  onVideoSourceChange(index: number, event: Event): void {
    const selectedValue = Number((event.target as HTMLSelectElement).value || 1);
    const fieldGroup = this.infoFields.at(index) as FormGroup;

    fieldGroup.get('videoUrlType')?.setValue(selectedValue);
    if (selectedValue === 1) {
      fieldGroup.patchValue({
        videoManualURL: '',
        isYouTube: false,
      });
    } else {
      fieldGroup.patchValue({
        videoURL: '',
        videoBlob: null,
      });
    }
  }

  handleVideoManualURLChange(index: number, event: Event): void {
    const value = sanitizeString((event.target as HTMLInputElement).value);
    const fieldGroup = this.infoFields.at(index) as FormGroup;
    fieldGroup.patchValue({
      videoManualURL: value,
      videoURL: '',
      videoBlob: null,
      isYouTube: isYouTubeUrl(value),
    });
  }

  removeMedia(index: number, field: 'imageURL' | 'videoURL'): void {
    const fieldGroup = this.infoFields.at(index) as FormGroup;
    if (field === 'imageURL') {
      fieldGroup.patchValue({
        imageURL: '',
        blob: null,
      });
      return;
    }

    fieldGroup.patchValue({
      videoURL: '',
      videoBlob: null,
      videoManualURL: '',
      isYouTube: false,
    });

    const input = document.getElementById(`btn-upload-video-${index}`) as HTMLInputElement | null;
    if (input) {
      input.value = '';
    }
  }

  sanitizeURL(mediaURL: string): any {
    if (!mediaURL) {
      return '';
    }

    if (mediaURL.match(/\.(jpeg|jpg|gif|png|webp)$/i)) {
      return this.sanitizer.bypassSecurityTrustUrl(mediaURL);
    }

    if (mediaURL.match(/\.(mp4|webm|ogg)$/i)) {
      return this.sanitizer.bypassSecurityTrustResourceUrl(mediaURL);
    }

    return this.sanitizer.bypassSecurityTrustUrl(mediaURL);
  }

  sanitizeVideoURL(videoURL: string): SafeUrl {
    if (this.isYouTubeVideo(videoURL)) {
      const embedUrl = videoURL.replace('watch?v=', 'embed/');
      return this.sanitizer.bypassSecurityTrustResourceUrl(embedUrl);
    }

    return this.sanitizer.bypassSecurityTrustResourceUrl(videoURL);
  }

  isYouTubeVideo(url: string): boolean {
    return isYouTubeUrl(url);
  }

  captureThumbnail(video: HTMLVideoElement): void {
    video.currentTime = 0;
    video.pause();
  }

  trackByLayoutOption(_: number, design: { typeId: number }): number {
    return Number(design.typeId || 0);
  }

  trackByIndex(index: number): number {
    return index;
  }

  onLayoutCardEnter(design: any, anchor: HTMLElement): void {
    if (this.isLayoutSelectionDisabled || this.usesTapPreviewMode) {
      return;
    }

    this.clearLayoutPreviewTimer();
    this.activeLayoutPreview = design;
    this.updateLayoutPreviewPosition(anchor);
  }

  onLayoutCardLeave(): void {
    if (this.usesTapPreviewMode) {
      return;
    }

    this.clearLayoutPreviewTimer();
    this.hideLayoutPreviewTimer = setTimeout(() => {
      this.hideLayoutPreview();
    }, 60);
  }

  openLayoutPreviewModal(design: any, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.isLayoutSelectionDisabled || !this.usesTapPreviewMode) {
      return;
    }

    this.previewModalDesign = design;
  }

  closeLayoutPreviewModal(): void {
    this.previewModalDesign = null;
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.evaluatePreviewInteractionMode();

    if (this.usesTapPreviewMode) {
      this.hideLayoutPreview();
      return;
    }

    if (this.activeLayoutPreview) {
      this.hideLayoutPreview();
    }
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.hideLayoutPreview();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.hideLayoutPreview();
    this.closeLayoutPreviewModal();
  }

  isCurrentStep(step: number): boolean {
    return this.currentStep === step;
  }

  isStepComplete(step: number): boolean {
    return step < this.currentStep && this.isStepValid(step);
  }

  isStepValid(step: number): boolean {
    switch (step) {
      case 1:
        return this.validateStepOne();
      case 2:
        return this.validateStepTwo();
      case 3:
        return this.validateStepThree();
      case 4:
        return this.validateStepFour();
      default:
        return true;
    }
  }

  getTabState(index: number): 'valid' | 'incomplete' | 'error' {
    const group = this.infoFields.at(index) as FormGroup;
    if (!group) {
      return 'incomplete';
    }

    if (group.invalid && (group.touched || this.submitAttempted || (this.navigationAttempted && this.currentStep === 4))) {
      return 'error';
    }

    return this.isInfoGroupComplete(group, index) ? 'valid' : 'incomplete';
  }

  shouldShowControlError(control: AbstractControl | null): boolean {
    return !!control && control.invalid && (control.touched || this.submitAttempted || this.navigationAttempted);
  }

  getInfoRemainingCharacters(index: number): number {
    return this.infoMaxLength - (this.infoCharacterCounts[index] || 0);
  }

  closeModal(): void {
    $('#successcomponentModal').modal('hide');
    $('#errorcomponentModal').modal('hide');
    $('#errorMaxMinItemModal').modal('hide');
    $('#errorCreateFileSizeModal').modal('hide');
    $('#errorRedirectUrl').modal('hide');
    this.closeLayoutPreviewModal();
  }

  onSuccessModalClose(): void {
    $('#successcomponentModal').modal('hide');
    if (!this.pendingSuccessAcknowledgement) {
      return;
    }

    this.pendingSuccessAcknowledgement = false;
    this.creationSuccess.emit();
  }

  private validateStepOne(): boolean {
    if (this.componentGroup.get('componentName')?.invalid || !this.selectedParentId) {
      return false;
    }

    if (this.selectedParentId !== 3) {
      return true;
    }

    if (!this.selectedOption) {
      return false;
    }

    const component = this.componentGroup.getRawValue();
    switch (this.selectedOption) {
      case 'collections':
        return Number(component.collectionId || 0) > 0;
      case 'categories':
        return Number(component.categoryId || 0) > 0;
      case 'subCategories':
        return Number(component.subCategoryId || 0) > 0;
      case 'subSubcategories':
        return Number(component.subSubCategoryId || 0) > 0;
      default:
        return true;
    }
  }

  private validateStepTwo(): boolean {
    if (!this.componentGroup.get('componentTypeId')?.valid) {
      return false;
    }

    if (!this.visibleFields.has('itemQuantity')) {
      return true;
    }

    return this.componentGroup.get('itemQuantity')?.valid ?? false;
  }

  private validateStepThree(): boolean {
    const controlsToCheck = ['title', 'subtitle', 'description', 'shopContactMail'];
    return controlsToCheck.every((controlName) => {
      if (!this.visibleFields.has(controlName as VisibleFieldKey)) {
        return true;
      }

      return this.componentGroup.get(controlName)?.valid ?? false;
    });
  }

  private validateStepFour(): boolean {
    if (!this.usesInfoTabs) {
      return true;
    }

    if (this.infoFields.length < this.minItems) {
      return false;
    }

    const firstInvalidIndex = this.infoFields.controls.findIndex((control) => control.invalid);
    if (firstInvalidIndex > -1) {
      this.selectedIndex = firstInvalidIndex;
      return false;
    }

    return this.infoFields.valid;
  }

  private markStepAsTouched(step: number): void {
    switch (step) {
      case 1:
        this.componentGroup.get('componentName')?.markAsTouched();
        break;
      case 2:
        this.componentGroup.get('componentTypeId')?.markAsTouched();
        this.componentGroup.get('itemQuantity')?.markAsTouched();
        break;
      case 3:
        ['title', 'subtitle', 'description', 'shopContactMail'].forEach((controlName) => {
          if (this.visibleFields.has(controlName as VisibleFieldKey)) {
            this.componentGroup.get(controlName)?.markAsTouched();
          }
        });
        break;
      case 4:
        this.infoFields.controls.forEach((control) => control.markAllAsTouched());
        break;
      default:
        break;
    }
  }

  private isInfoGroupComplete(group: FormGroup, index: number): boolean {
    if (this.visibleFields.has('infoTitle') && !hasMeaningfulText(group.get('infoTitle')?.value)) {
      return false;
    }

    if (this.visibleFields.has('infoDescription') && !hasMeaningfulText(group.get('infoDescription')?.value)) {
      return false;
    }

    if (this.visibleFields.has('urlName') && !sanitizeString(group.get('urlName')?.value)) {
      return false;
    }

    if (this.visibleFields.has('infoImageUrl') && !sanitizeString(group.get('imageURL')?.value)) {
      return false;
    }

    if (group.get('redirectUrl')?.invalid && sanitizeString(group.get('redirectUrl')?.value)) {
      return false;
    }

    return true;
  }

  private async convertImageToWebP(file: File, maxSize = 1920, quality = 0.8): Promise<File> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxSize || height > maxSize) {
          const scale = maxSize / Math.max(width, height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject('No se pudo obtener el contexto del canvas.');
          return;
        }

        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject('No se pudo generar el blob WebP.');
              return;
            }

            resolve(new File([blob], file.name.replace(/\.\w+$/, '.webp'), { type: 'image/webp' }));
          },
          'image/webp',
          quality
        );
      };
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });
  }

  private async uploadFileToS3(fileObj: { file: File; key: string }): Promise<string> {
    const uploadResponse: any = await this.lambdaService.uploadFile(fileObj.key, fileObj.file, { position: 1, type: 'original' });
    return uploadResponse.imageURL || uploadResponse.videoURL;
  }

  private async uploadComponentFiles(): Promise<void> {
    for (const control of this.infoFields.controls) {
      const fieldGroup = control as FormGroup;
      const imageBlob = fieldGroup.get('blob')?.value;
      const videoBlob = fieldGroup.get('videoBlob')?.value;
      const videoType = Number(fieldGroup.get('videoUrlType')?.value || 0);

      if (imageBlob?.file && imageBlob?.key) {
        const uploadedImage = await this.uploadFileToS3(imageBlob);
        fieldGroup.patchValue({
          imageURL: uploadedImage,
          blob: null,
        });
      }

      if (videoType === 1 && videoBlob?.file && videoBlob?.key) {
        const uploadedVideo = await this.uploadFileToS3(videoBlob);
        fieldGroup.patchValue({
          videoURL: uploadedVideo,
          videoBlob: null,
        });
      }
    }
  }

  private resolveSubscriptionsAllowed(): string {
    if (this.activeSubscriptions === 0) {
      return '0';
    }

    return this.selectedSubscriptions.length > 0 ? this.selectedSubscriptions.join(',') : '';
  }

  private resetSelections(): void {
    this.componentGroup.patchValue({
      categoryId: 0,
      subCategoryId: 0,
      subSubCategoryId: 0,
      collectionId: 0,
    });
    this.designEnabled = false;
  }

  private resetDynamicState(): void {
    const currentName = this.componentGroup.get('componentName')?.value || '';
    const pageId = parseInt(this.pageId || '0', 10);

    this.componentForm = createComponentForm(this.fb);
    this.componentForm.get('component')?.patchValue({
      componentName: currentName,
      pageId,
    });

    this.selectedDesign = null;
    this.selectedDesignDescription = '';
    this.selectedDesignImageUrl = '';
    this.selectedOption = '';
    this.productComponentType = 0;
    this.minItems = 0;
    this.maxItems = 0;
    this.currentStep = 1;
    this.selectedIndex = 0;
    this.remainingCharacters = this.maxLength;
    this.infoCharacterCounts = [];
    this.visibleFields = new Set<VisibleFieldKey>();
    this.submitAttempted = false;
    this.navigationAttempted = false;
  }

  private focusItemPanel(index: number): void {
    if (typeof window === 'undefined') {
      return;
    }

    window.requestAnimationFrame(() => {
      const panel = document.getElementById(`create-item-panel-${index}`);
      panel?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  private getUniqueParentIds(): any[] {
    return Array.from(new Set(this.componentTypes.map((item) => item.componentParentId)))
      .filter((parentId) => this.parentIdNames.hasOwnProperty(parentId))
      .map((parentId) => {
        const firstComponentWithType = this.componentTypes.find((item) => item.componentParentId === parentId);
        return { id: parentId, name: firstComponentWithType ? this.parentIdNames[parentId] : '' };
      });
  }

  private getDesignsByParentId(): Record<number, any[]> {
    return this.componentTypes.reduce((acc, curr) => {
      if (!acc[curr.componentParentId]) {
        acc[curr.componentParentId] = [];
      }

      acc[curr.componentParentId].push({
        id: curr.componentTypeDesignId,
        name: curr.componentTypeName,
        typeId: curr.componentTypeId,
        description: curr.componentTypeDescription,
        imageUrl: curr.imageUrl,
      });

      return acc;
    }, {} as Record<number, any[]>);
  }

  private applyComponentTypeSelection(componentTypeId: number): void {
    this.componentGroup.get('componentTypeId')?.setValue(componentTypeId);

    this.selectedDesign =
      this.groupedDesigns[this.selectedParentId]?.find((design: { typeId: number }) => design.typeId === componentTypeId) || null;
    this.selectedDesignDescription = this.selectedDesign?.description || '';
    this.selectedDesignImageUrl = this.selectedDesign?.imageUrl || '';

    const componentType = this.componentTypes.find((ct) => ct.componentTypeId === componentTypeId);
    if (!componentType) {
      this.errorMessage = this.translate.instant('COMPONENT_TYPE_NOT_FOUND');
      $('#errorMaxMinItemModal').modal('show');
      return;
    }

    this.minItems = Number(componentType.minItems || 0);
    this.maxItems = Number(componentType.maxItems || 0);
    this.selectedIndex = 0;
    this.updateVisibleFields();

    this.infoFields.clear();
    this.infoCharacterCounts = [];
    for (let index = 0; index < this.minItems; index += 1) {
      this.addNewField(false);
    }
  }

  private evaluatePreviewInteractionMode(): void {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      this.usesTapPreviewMode = false;
      return;
    }

    this.usesTapPreviewMode = window.matchMedia('(hover: none), (pointer: coarse), (max-width: 1024px)').matches;
  }

  private updateLayoutPreviewPosition(anchor: HTMLElement): void {
    if (typeof window === 'undefined') {
      return;
    }

    const rect = anchor.getBoundingClientRect();
    const previewWidth = Math.min(320, Math.max(260, window.innerWidth - 32));
    const gutter = 16;
    const availableAbove = rect.top - gutter - 12;
    const previewHeight = Math.min(320, Math.max(180, availableAbove || 0));
    const horizontalCenter = rect.left + (rect.width / 2) - (previewWidth / 2);
    const maxLeft = Math.max(gutter, window.innerWidth - previewWidth - gutter);
    const left = Math.min(Math.max(gutter, horizontalCenter), maxLeft);
    const top = Math.max(gutter, rect.top - previewHeight - 12);

    this.layoutPreviewStyle = {
      left: `${left}px`,
      maxHeight: `${previewHeight}px`,
      top: `${top}px`,
      width: `${previewWidth}px`,
    };
  }

  private hideLayoutPreview(): void {
    this.clearLayoutPreviewTimer();
    this.activeLayoutPreview = null;
    this.layoutPreviewStyle = {};
  }

  private clearLayoutPreviewTimer(): void {
    if (this.hideLayoutPreviewTimer) {
      clearTimeout(this.hideLayoutPreviewTimer);
      this.hideLayoutPreviewTimer = null;
    }
  }
}
